// src/utils/trackVisitor.js
import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// ✅ Get today's date in YYYY-MM-DD format
const getTodayKey = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// ✅ Generate unique visitor ID (stored in localStorage)
const getVisitorId = () => {
  let id = localStorage.getItem('visitorId');
  if (!id) {
    id = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('visitorId', id);
  }
  return id;
};

// ✅ Main tracking function
export const trackVisitor = async (pagePath = '/') => {
  try {
    const visitorId = getVisitorId();
    const todayKey = getTodayKey();
    const analyticsRef = doc(db, 'analytics', 'main');

    // 1. Ensure analytics document exists
    const analyticsSnap = await getDoc(analyticsRef);

    if (!analyticsSnap.exists()) {
      await setDoc(analyticsRef, {
        totalVisitors: 1,
        totalPageViews: 1,
        todayVisitors: 1,
        todayDate: todayKey,
        dailyVisits: {
          [todayKey]: 1,
        },
        uniqueVisitors: [visitorId],
        lastUpdated: serverTimestamp(),
      });
      return;
    }

    const data = analyticsSnap.data();

    // 2. Check if this is a new visitor today
    const todayVisits = data.dailyVisits?.[todayKey] || 0;
    const isNewVisitorToday = !sessionStorage.getItem(`visited_${todayKey}`);

    // 3. Update counters
    const updates = {
      totalPageViews: increment(1),
      lastUpdated: serverTimestamp(),
    };

    // If new visitor today
    if (isNewVisitorToday) {
      sessionStorage.setItem(`visited_${todayKey}`, 'true');

      // Check if today key changed
      if (data.todayDate !== todayKey) {
        updates.todayDate = todayKey;
        updates.todayVisitors = 1;
      } else {
        updates.todayVisitors = increment(1);
      }

      // Update daily visits
      updates[`dailyVisits.${todayKey}`] = todayVisits + 1;

      // Add to unique visitors if new
      const uniqueVisitors = data.uniqueVisitors || [];
      if (!uniqueVisitors.includes(visitorId)) {
        updates.uniqueVisitors = [...uniqueVisitors, visitorId];
        updates.totalVisitors = increment(1);
      }
    }

    // 4. Live visitor tracking (last 5 minutes)
    const now = Date.now();
    const liveVisitors = data.liveVisitors || {};
    liveVisitors[visitorId] = now;

    // Clean up old live visitors (>5 min)
    Object.keys(liveVisitors).forEach(id => {
      if (now - liveVisitors[id] > 5 * 60 * 1000) {
        delete liveVisitors[id];
      }
    });

    updates.liveVisitors = liveVisitors;

    await updateDoc(analyticsRef, updates);

  } catch (error) {
    console.error('Track visitor error:', error);
  }
};

// ✅ Get visitor stats
export const getVisitorStats = async () => {
  try {
    const analyticsRef = doc(db, 'analytics', 'main');
    const snap = await getDoc(analyticsRef);

    if (!snap.exists()) {
      return {
        totalVisitors: 0,
        totalPageViews: 0,
        todayVisitors: 0,
        liveVisitors: 0,
        dailyVisits: {},
      };
    }

    const data = snap.data();

    // Count live visitors (last 5 min)
    const now = Date.now();
    const liveVisitors = Object.values(data.liveVisitors || {}).filter(
      timestamp => now - timestamp < 5 * 60 * 1000
    ).length;

    return {
      totalVisitors: data.totalVisitors || 0,
      totalPageViews: data.totalPageViews || 0,
      todayVisitors: data.todayVisitors || 0,
      liveVisitors,
      dailyVisits: data.dailyVisits || {},
    };
  } catch (error) {
    console.error('Get visitor stats error:', error);
    return {
      totalVisitors: 0,
      totalPageViews: 0,
      todayVisitors: 0,
      liveVisitors: 0,
      dailyVisits: {},
    };
  }
};

export default trackVisitor;